<script setup>
import { computed } from 'vue';
import VueApexCharts from 'vue3-apexcharts';

const props = defineProps({
  data: {
    type: Array,
    default: () => [],
    // [{ 운세: '가족운', 점수: 4.32 }, ...]
  },
  maxScore: {
    type: Number,
    default: 5,
  },
  barColor: {
    type: String,
    default: '#88c34a',
  },
});

// 데이터를 퍼센트로 변환
const chartData = computed(() => {
  return props.data.map((item) => ({
    x: item.운세,
    y: Math.round((item.점수 / props.maxScore) * 100),
  }));
});

const categories = computed(() => props.data.map((item) => item.운세));

// 활동 데이터 존재 여부
const hasActivity = computed(() => props.data.some((item) => item.활동));

// 활동 매핑 (운세 → 활동)
const activityMap = computed(() => {
  const map = {};
  props.data.forEach((item) => {
    if (item.활동) map[item.운세] = item.활동;
  });
  return map;
});

// 활동점수 가로선 annotations
const activityAnnotations = computed(() => {
  if (!hasActivity.value) return [];
  return props.data
    .filter((item) => item.활동점수 != null)
    .map((item) => ({
      x: item.운세,
      y: Math.round((item.활동점수 / props.maxScore) * 100),
      marker: { size: 0 },
      customSVG: {
        SVG: `<line x1="-14" y1="0" x2="14" y2="0" stroke="#eb4c10" stroke-width="2.5" stroke-linecap="round"/>`,
        offsetX: 0,
        offsetY: 0,
      },
    }));
});

const series = computed(() => [
  {
    name: '점수',
    data: chartData.value,
  },
]);

const chartOptions = computed(() => ({
  chart: {
    type: 'bar',
    toolbar: { show: false },
    zoom: { enabled: false },
    parentHeightOffset: 0,
  },
  plotOptions: {
    bar: {
      borderRadius: 4,
      columnWidth: '28px',
      dataLabels: {
        position: 'top',
      },
    },
  },
  dataLabels: {
    enabled: false,
  },
  colors: [props.barColor],
  xaxis: {
    categories: categories.value,
    axisTicks: { show: false },
    axisBorder: { show: false },
    labels: {
      ...(hasActivity.value && {
        formatter: (val) => {
          const activity = activityMap.value[val];
          return activity ? [val, activity] : val;
        },
      }),
      minHeight: 40,
      style: {
        colors: '#333',
        fontFamily: 'Pretendard',
        fontSize: '13px',
        fontWeight: '500',
      },
    },
  },
  yaxis: {
    min: 0,
    max: 100,
    tickAmount: 4,
    labels: {
      minWidth: 40,
      formatter: (val) => `${val}%`,
      style: {
        colors: '#757575',
        fontFamily: 'Pretendard',
        fontSize: '12px',
      },
    },
  },
  grid: {
    borderColor: '#ebebeb',
    strokeDashArray: 0,
    padding: {
      top: 0,
      bottom: 0,
      left: 8,
      right: 8,
    },
  },
  tooltip: {
    enabled: true,
    shared: false,
    intersect: true,
    ...(hasActivity.value
      ? {
          custom: ({ dataPointIndex }) => {
            const item = props.data[dataPointIndex];
            const score = Math.round((item.점수 / props.maxScore) * 100);
            const activity = item.활동 || '';
            const actScore =
              item.활동점수 != null
                ? Math.round((item.활동점수 / props.maxScore) * 100)
                : null;
            return `<div style="padding:8px 12px;font-family:Pretendard;font-size:13px;line-height:1.6">
              <div><b>${item.운세}</b> ${score}%</div>
              ${actScore != null ? `<div style="color:#eb4c10"><b>${activity}</b> ${actScore}%</div>` : ''}
            </div>`;
          },
        }
      : {
          y: {
            formatter: (val) => `${val}%`,
          },
        }),
  },
  ...(hasActivity.value && {
    annotations: {
      points: activityAnnotations.value,
    },
  }),
}));
</script>

<template>
  <div class="yearly-fortune-bar-chart">
    <VueApexCharts type="bar" height="220" :options="chartOptions" :series="series" />
  </div>
</template>

<style scoped lang="scss">
.yearly-fortune-bar-chart {
  width: 100%;

  :deep(.apexcharts-canvas) {
    max-width: 100% !important;
    width: 100% !important;
  }

  :deep(.apexcharts-xaxis-label tspan:nth-child(2)) {
    fill: #eb4c10;
  }
}
</style>
